import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import { AxiosError } from 'axios';
import { isNil, join, map, pipe, replace, split, toLower, trim } from 'ramda';

import {
  firebaseDeleteApi,
  firebaseGetApi,
  firebasePatchApi,
  firebasePostApi,
} from '../../../api/firebaseApi';
import i18n from '../../../i18n';
import { getTrialLimitType } from '../../rentNavigator/data/rentNavigatorDataProvider';

const BASE_URL = '/ai-reports';

export const aiReportsEndpoints = {
  locations: `${BASE_URL}/locations`,
  location: `${BASE_URL}/location`,
  powerpointPreview: `${BASE_URL}/location/preview`,
  countryFromPoint: `${BASE_URL}/location/country`,
  report: `${BASE_URL}/location/report`,
  properties: `${BASE_URL}/properties`,
};

// Query keys
export const aiReportsLocationsQueryKey = 'ai-reports-locations';
export const aiReportsLocationQueryKey = 'ai-reports-location';
const addLocationMutateKey = 'ai-reports-add-location';
const updateLocationMutateKey = 'ai-reports-update-location';
const deleteLocationMutateKey = 'ai-reports-delete-location';
const generateReportMutateKey = 'ai-reports-generate-report';

const cleanLocationNameForFileDownload = pipe(
  trim,
  split(/\s+/),
  map(toLower),
  join('-'),
  replace(/[^a-z\u00e4\u00f6\u00fc0-9\s-]/g, ''),
  replace(/-+/g, '-'),
);

// Types for AI Reports
interface IGeoJSONGeometry {
  type:
    | 'Point'
    | 'Polygon'
    | 'MultiPolygon'
    | 'LineString'
    | 'MultiLineString'
    | 'MultiPoint'
    | 'GeometryCollection';
  coordinates: number[] | number[][] | number[][][] | number[][][][];
}

interface IGeoJSONFeature {
  type: 'Feature';
  geometry: IGeoJSONGeometry;
  properties: Record<string, any>;
}

export interface IAiReportLocation {
  id: string | number;
  name: string;
  language?: string;
  address?: string;
  area_code?: string;
  country_code?: string;
  municipality?: string;
  geometry?: IGeoJSONFeature;
  reportStatus?: 'generating' | 'done' | 'failed' | null;
}

// Hook for fetching locations list
export const useAiReportsLocations = () => {
  const fetchLocations = ({ signal }: { signal: any }) =>
    firebaseGetApi(aiReportsEndpoints.locations, {}, { signal });

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: [aiReportsLocationsQueryKey],
    queryFn: fetchLocations,
  });

  const locations = data?.locations || [];

  return {
    locations,
    isLoading: isLoading || isFetching,
    error,
    fetchLocations: refetch,
  };
};

export const downloadAiReportsReport = async ({
  locationId,
  locationName,
  fileName,
}: {
  locationId: number | string;
  locationName?: string;
  fileName?: string;
}) => {
  const data = await firebaseGetApi(aiReportsEndpoints.report, {
    locationId,
    responseType: 'blob',
  });

  const blob = new Blob([data], {
    type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const safeFileName = cleanLocationNameForFileDownload(locationName || '');
  const resolvedFileName =
    fileName || `${safeFileName || 'chaos-ai-report'}.pptx`;
  link.download = resolvedFileName;
  link.click();
  window.URL.revokeObjectURL(url);
};

const AI_REPORTS_TRIAL_LIMIT_MESSAGES: Record<string, string> = {
  activeLocations: 'trialLimits.activeLocationsLimitMessage',
  createReport: 'trialLimits.createReportLimitMessage',
};

export const notifyAiReportsTrialLimit = (error: unknown): boolean => {
  const limitType = getTrialLimitType(error);
  const messageKey = limitType && AI_REPORTS_TRIAL_LIMIT_MESSAGES[limitType];
  if (!messageKey) return false;

  notification.error({
    key: 'ai-reports-trial-limit',
    title: i18n.t('aiReports:trialLimits.title'),
    description: i18n.t(`aiReports:${messageKey}`),
    placement: 'top',
    duration: 10,
  });
  return true;
};

export const useAiReportsAddLocation = (isFirstPage: boolean = false) => {
  const queryClient = useQueryClient();

  const addLocationApiCall = (payload: any) =>
    firebasePostApi(aiReportsEndpoints.location, payload);

  const { mutateAsync, isPending } = useMutation({
    mutationKey: [addLocationMutateKey],
    mutationFn: addLocationApiCall,
    onError: notifyAiReportsTrialLimit,
    onSuccess: (newLocation) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [aiReportsLocationsQueryKey],
          exact: true,
        }),
        isFirstPage &&
          queryClient.setQueryData(
            [aiReportsLocationQueryKey, newLocation.id],
            newLocation,
          ),
      ]),
  });

  return { addLocation: mutateAsync, isLoading: isPending };
};

export const useAiReportsLocation = ({
  locationId,
}: {
  locationId: number | null;
}) => {
  const fetchLocation = ({ signal }: { signal: any }) =>
    firebaseGetApi(aiReportsEndpoints.location, { locationId }, { signal });

  const isEnabled = Boolean(locationId);

  const { data, isLoading, isFetching, error, isError } = useQuery({
    queryKey: [aiReportsLocationQueryKey, locationId],
    queryFn: fetchLocation,
    enabled: isEnabled,
  });

  return {
    location: data,
    isLoading: isLoading || isFetching,
    error: error as AxiosError,
    isError,
  };
};

// Hook for updating location
export const useAiReportsUpdateLocation = () => {
  const queryClient = useQueryClient();

  const updateLocationApiCall = (
    payload: { id: string | number } & Partial<IAiReportLocation>,
  ) => firebasePatchApi(aiReportsEndpoints.location, payload);

  const { mutateAsync, isPending } = useMutation({
    mutationKey: [updateLocationMutateKey],
    mutationFn: updateLocationApiCall,
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: [aiReportsLocationsQueryKey],
        exact: true,
      }),
  });

  return { updateLocation: mutateAsync, isLoading: isPending };
};

// Hook for deleting location
export const useAiReportsDeleteLocation = () => {
  const queryClient = useQueryClient();

  const deleteLocationApiCall = (id: string | number) =>
    firebaseDeleteApi(aiReportsEndpoints.location, { id });

  const { mutateAsync, isPending } = useMutation({
    mutationKey: [deleteLocationMutateKey],
    mutationFn: deleteLocationApiCall,
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: [aiReportsLocationsQueryKey],
        exact: true,
      }),
  });

  return { deleteLocation: mutateAsync, isLoading: isPending };
};

export const useAiReportsGenerateReport = () => {
  const queryClient = useQueryClient();

  const generateReportApiCall = (payload: { locationId: string | number }) =>
    firebasePostApi(aiReportsEndpoints.report, payload);

  const { mutateAsync, isPending } = useMutation({
    mutationKey: [generateReportMutateKey],
    mutationFn: generateReportApiCall,
    onMutate: ({ locationId }) => {
      queryClient.setQueryData([aiReportsLocationsQueryKey], (oldData: any) => {
        if (!oldData?.locations) return oldData;
        return {
          ...oldData,
          locations: oldData.locations.map((location: IAiReportLocation) =>
            String(location.id) === String(locationId)
              ? { ...location, reportStatus: 'generating' }
              : location,
          ),
        };
      });
    },
    onError: (error) => {
      notifyAiReportsTrialLimit(error);
      return queryClient.invalidateQueries({
        queryKey: [aiReportsLocationsQueryKey],
        exact: true,
      });
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: [aiReportsLocationsQueryKey],
        exact: true,
      }),
  });

  return { createAiReportsReport: mutateAsync, isLoading: isPending };
};

export const useAiReportsPowerpointPreview = (locationId: number | null) => {
  const fetchPowerpointPreview = ({ signal }: { signal: any }) =>
    firebaseGetApi(
      aiReportsEndpoints.powerpointPreview,
      { locationId },
      { signal },
    );

  const isEnabled = !isNil(locationId) && locationId !== -1;
  const { data, isLoading, error } = useQuery({
    queryKey: [aiReportsLocationQueryKey, locationId, 'powerpointPreview'],
    queryFn: fetchPowerpointPreview,
    enabled: isEnabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    url: data?.url || null,
    isLoading,
    error,
  };
};

export const useCountryFromPoint = () => {
  const fetchCountryFromPoint = (point: { lat: number; lng: number }) => {
    const payload = {
      lat: point?.lat,
      lng: point?.lng,
    };

    return firebasePostApi(aiReportsEndpoints.countryFromPoint, payload);
  };

  const { mutateAsync } = useMutation({
    mutationKey: ['get-country-from-point'],
    mutationFn: fetchCountryFromPoint,
  });

  return {
    validatePointPlacement: mutateAsync,
  };
};
